/**
 * bl-predictive-search.js — Shopify Predictive Search presentation only.
 *
 * Shopify performs the search: this module requests the native predictive
 * search endpoint (routes.predictive_search_url) rendered through
 * sections/bl-predictive-search.liquid and injects the returned Liquid markup.
 * No product data is duplicated into JavaScript, no index is built, no external
 * service is contacted and no global state is created.
 *
 * Without this file the search form is a plain GET form to Shopify's search
 * URL and the whole search experience still works.
 *
 * Behaviour: debounce, abortable fetch, loading / empty / error states,
 * ArrowUp/ArrowDown suggestion navigation, Escape closes, Enter submits.
 */

const BL = window.BrightLeaf;
const DEBOUNCE = 250;
const MIN_LENGTH = 2;

BL.components.register('predictive-search', (root) => {
  const input = root.querySelector('[data-bl-search-input]');
  const results = root.querySelector('[data-bl-search-results]');
  const status = root.querySelector('[data-bl-search-status]');
  if (!input || !results) return undefined;

  const endpoint = root.dataset.blPredictiveUrl || '/search/suggest';
  const sectionId = root.dataset.blPredictiveSection || 'bl-predictive-search';
  const limit = root.dataset.blPredictiveLimit || '5';
  const types = root.dataset.blPredictiveTypes || 'product';

  let controller = null;
  let timer = null;
  let options = [];
  let active = -1;

  const setStatus = (text) => {
    if (status) status.textContent = text || '';
  };

  const collect = () => {
    options = [...results.querySelectorAll('[data-bl-predictive-option]')];
    active = -1;
  };

  const close = () => {
    window.clearTimeout(timer);
    controller?.abort();
    controller = null;
    results.innerHTML = '';
    results.hidden = true;
    root.classList.remove('bl-search--open');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    options = [];
    active = -1;
  };

  const highlight = (index) => {
    options.forEach((el, i) => el.classList.toggle('is-active', i === index));
    active = index;
    const el = options[index];
    if (el) {
      if (!el.id) el.id = `bl-predictive-option-${index}`;
      input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  async function request(term) {
    controller?.abort();
    controller = new AbortController();

    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('resources[type]', types);
    url.searchParams.set('resources[limit]', limit);

    root.classList.add('bl-search--loading');
    setStatus(root.dataset.blLabelLoading || '');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      results.innerHTML = await response.text();
      results.hidden = false;
      root.classList.add('bl-search--open');
      input.setAttribute('aria-expanded', 'true');
      collect();
      setStatus(
        results.querySelector('[data-bl-predictive-empty]')
          ? root.dataset.blLabelEmpty || ''
          : (root.dataset.blLabelCount || '').replace('{{ count }}', String(options.length)),
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      results.innerHTML = `<p class="bl-predictive__error">${root.dataset.blLabelError || ''}</p>`;
      results.hidden = false;
      collect();
      setStatus(root.dataset.blLabelError || '');
    } finally {
      root.classList.remove('bl-search--loading');
    }
  }

  const onInput = () => {
    const term = input.value.trim();
    window.clearTimeout(timer);
    if (term.length < MIN_LENGTH) {
      close();
      return;
    }
    timer = window.setTimeout(() => request(term), DEBOUNCE);
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (results.hidden) return;
      event.stopPropagation();
      close();
      input.focus();
      return;
    }
    if (event.key === 'Enter') {
      /* Enter on a highlighted suggestion follows it; otherwise the native
         form submission to Shopify's search URL happens untouched. */
      if (active > -1 && options[active]) {
        event.preventDefault();
        window.location.assign(options[active].href);
      }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!options.length) return;
    event.preventDefault();
    const next = event.key === 'ArrowDown' ? active + 1 : active - 1;
    highlight((next + options.length) % options.length);
  };

  const onFocusOut = (event) => {
    if (!root.contains(event.relatedTarget)) close();
  };

  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKeydown);
  root.addEventListener('focusout', onFocusOut);

  return () => {
    close();
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKeydown);
    root.removeEventListener('focusout', onFocusOut);
  };
});
